import {
  type ChaveDaFaixa,
  type EfeitoDeRegra,
  PONTOS_BASE_NEUTROS,
  TAMANHO_MAXIMO_DE_PAGINA,
  TIPOS_DE_TAXA_DE_MULTA,
  precificacao,
} from '@locaveri/shared'
import {
  type QueryClient,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import { apiDelete, apiGet, apiPatch, apiPost, apiRequest } from '@/lib/api'
import { formatarData, SEM_VALOR } from '@/lib/datas'
import { ROTAS_DE_PRECOS } from '@/lib/endpoints'
import { montarQuery } from '@/lib/query-string'

/**
 * Preço — tabelas, tarifas por faixa, as quatro camadas de regra, a política de
 * locação e o simulador de diárias.
 *
 * ---------------------------------------------------------------------------
 * A REGRA DE OURO, NA CAMADA DE PREÇO
 * ---------------------------------------------------------------------------
 * A tarifa é da CATEGORIA, nunca do veículo. `tarifaSchema` tem `categoryId` e
 * não tem — nem pode ganhar — `vehicleId`: o modelo e a placa são decididos na
 * alocação, depois de o preço já estar fechado. Se um dia aparecer preço por
 * veículo aqui, a Fase 4 herda duas fontes de verdade para o mesmo número.
 *
 * ---------------------------------------------------------------------------
 * O PREÇO É DETERMINÍSTICO — E ESTA CAMADA NÃO CALCULA NADA
 * ---------------------------------------------------------------------------
 * Invariante nº 3: quem decide preço é o motor do servidor. Aqui não se soma
 * diária, não se aplica multiplicador e não se estima total — o painel LÊ o
 * número já calculado e o formata. Toda aritmética monetária que aparecesse
 * neste arquivo seria uma segunda implementação do motor, divergindo em
 * silêncio na primeira regra nova.
 *
 * Por isso os únicos "cálculos" daqui são de APRESENTAÇÃO sobre inteiros que
 * não são dinheiro: pontos-base viram `1,25×` e `50%`, e nada mais.
 *
 * ---------------------------------------------------------------------------
 * O QUE VEM DE ONDE
 * ---------------------------------------------------------------------------
 * O modelo de LEITURA é declarado aqui (Zod local), espelhando os DTOs de saída
 * da API — ele traz a categoria já resolvida e as contagens agregadas, e não é
 * o contrato de ENTRADA, que é o que `@locaveri/shared` guarda. Os enums, os
 * rótulos e as constantes de domínio vêm sempre do pacote compartilhado: faixa,
 * efeito e multiplicador máximo redigitados aqui seriam divergência esperando o
 * dia em que o motor ganhar uma faixa.
 *
 * Dinheiro chega como STRING de centavos (invariante nº 1) e é formatado com
 * `lib/dinheiro`. Nenhum valor monetário vira `number` no caminho.
 */

// ---------------------------------------------------------------------------
// Envelope de listagem
// ---------------------------------------------------------------------------

/**
 * A página que a API devolve. `paginas` vem do servidor junto do total — é ele
 * que faz a divisão, e refazê-la aqui daria uma resposta diferente na primeira
 * vez que o teto de página mudasse.
 *
 * Exportada porque `./cotacao` monta os mesmos quatro envelopes (bloqueios,
 * prestadores, lista de espera, overbooking) e uma segunda cópia do formato
 * seria a que alguém esqueceria de corrigir.
 */
export function paginaDe<T extends z.ZodType>(
  item: T,
): z.ZodObject<{
  itens: z.ZodArray<T>
  total: z.ZodNumber
  pagina: z.ZodNumber
  tamanho: z.ZodNumber
  paginas: z.ZodNumber
}> {
  return z.object({
    itens: z.array(item),
    total: z.number().int().nonnegative(),
    pagina: z.number().int().positive(),
    tamanho: z.number().int().positive(),
    paginas: z.number().int().nonnegative(),
  })
}

/**
 * O mínimo para a tela nomear a categoria sem carregar a frota inteira. `null`
 * em toda regra significa "vale para TODAS as categorias" — e é por isso que o
 * campo é anulável e não ausente: a diferença entre "todas" e "esqueceram de
 * preencher" é o que a tela precisa dizer em voz alta.
 */
export const resumoDaCategoriaSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  nome: z.string(),
})

export type ResumoDaCategoria = z.infer<typeof resumoDaCategoriaSchema>

// ---------------------------------------------------------------------------
// Tabela de preço
// ---------------------------------------------------------------------------

/**
 * Uma tabela de preço.
 *
 * As quatro contagens existem porque uma tabela sem elas é indistinguível de
 * outra na listagem: `tarifas` diz se ela foi preenchida,
 * `categoriasPrecificadas` diz quanto do catálogo ela cobre, `sazonalidades`
 * diz se há regra pendurada nela e `contasNegociadas` diz quantos clientes
 * corporativos quebram se ela for desativada.
 */
export const tabelaDePrecoSchema = z.object({
  id: z.uuid(),
  nome: z.string(),
  descricao: z.string().nullable(),
  /** A da vitrine. Exatamente uma ativa por locadora — índice único no banco. */
  padrao: z.boolean(),
  /** Data de calendário (`AAAA-MM-DD`), não instante: vigência não tem hora. */
  vigenteDe: z.string(),
  /** `null` = sem prazo. */
  vigenteAte: z.string().nullable(),
  ativo: z.boolean(),
  /** Linhas de tarifa somando todas as categorias. */
  tarifas: z.number().int().nonnegative(),
  categoriasPrecificadas: z.number().int().nonnegative(),
  sazonalidades: z.number().int().nonnegative(),
  /** Clientes corporativos que a usam como tabela negociada. */
  contasNegociadas: z.number().int().nonnegative(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type TabelaDePreco = z.infer<typeof tabelaDePrecoSchema>

export const paginaDeTabelasSchema = paginaDe(tabelaDePrecoSchema)

export type PaginaDeTabelas = z.infer<typeof paginaDeTabelasSchema>

// ---------------------------------------------------------------------------
// Tarifa por faixa de duração
// ---------------------------------------------------------------------------

/** Uma linha de tarifa. `diaria` é a diária CHEIA da faixa, nunca um delta. */
export const tarifaSchema = z.object({
  id: z.uuid(),
  priceTableId: z.uuid(),
  categoryId: z.uuid(),
  categoria: resumoDaCategoriaSchema,
  faixa: precificacao.chaveDaFaixaSchema,
  /** Centavos como string decimal (o wire de `Money`). */
  diaria: z.string(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type Tarifa = z.infer<typeof tarifaSchema>

export const paginaDeTarifasSchema = paginaDe(tarifaSchema)

export type PaginaDeTarifas = z.infer<typeof paginaDeTarifasSchema>

/**
 * As tarifas de UMA categoria numa tabela, na ordem das faixas do motor.
 *
 * `faixasFaltantes` é a informação que uma listagem de linhas não consegue
 * carregar: ausência não aparece numa lista. Sem a tarifa da faixa, uma locação
 * daquela duração simplesmente não é cotável — e hoje isso só apareceria no dia
 * em que um cliente pedisse sete diárias.
 */
export const tarifasDaCategoriaSchema = z.object({
  categoria: resumoDaCategoriaSchema,
  itens: z.array(
    z.object({
      id: z.uuid(),
      faixa: precificacao.chaveDaFaixaSchema,
      diaria: z.string(),
    }),
  ),
  faixasFaltantes: z.array(precificacao.chaveDaFaixaSchema),
  /**
   * ATENÇÃO AO SENTIDO: `true` é o estado BOM — a diária não sobe ao alongar a
   * locação, que é a promessa que o cliente lê na tabela. `false` é o alarme, e
   * a tela precisa marcá-lo: o `PUT` recusa gravar tabela crescente, então um
   * `false` aqui veio por outro caminho (seed, importação, tabela antiga
   * migrada) e ninguém mais vai reparar.
   */
  naoCrescente: z.boolean(),
})

export type TarifasDaCategoria = z.infer<typeof tarifasDaCategoriaSchema>

export const tarifasDaTabelaSchema = z.object({
  priceTableId: z.uuid(),
  itens: z.array(tarifasDaCategoriaSchema),
})

export type TarifasDaTabela = z.infer<typeof tarifasDaTabelaSchema>

// ---------------------------------------------------------------------------
// Camada 2 — sazonalidade
// ---------------------------------------------------------------------------

export const sazonalidadeSchema = z.object({
  id: z.uuid(),
  /** `null` = vale em qualquer tabela de preço. */
  priceTableId: z.uuid().nullable(),
  /** `null` = vale para todas as categorias. */
  categoryId: z.uuid().nullable(),
  categoria: resumoDaCategoriaSchema.nullable(),
  nome: z.string(),
  inicio: z.string(),
  /** Inclusive: uma regra de 20 a 27 pega o dia 27. */
  fim: z.string(),
  efeito: precificacao.efeitoDeRegraSchema,
  /** Pontos-base: 12000 = 1,2×. `PONTOS_BASE_NEUTROS` (10000) não mexe no preço. */
  multiplicadorEmPontosBase: z.number().int(),
  /** A diária cheia quando o efeito é `FIXO`. Centavos como string. */
  valorFixo: z.string().nullable(),
  /** Maior vence; no empate, o período mais curto. Quem decide é o motor. */
  prioridade: z.number().int(),
  ativo: z.boolean(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type Sazonalidade = z.infer<typeof sazonalidadeSchema>

export const paginaDeSazonalidadesSchema = paginaDe(sazonalidadeSchema)

export type PaginaDeSazonalidades = z.infer<typeof paginaDeSazonalidadesSchema>

// ---------------------------------------------------------------------------
// Camada 3 — dia da semana
// ---------------------------------------------------------------------------

export const regraDeDiaDaSemanaSchema = z.object({
  id: z.uuid(),
  categoryId: z.uuid().nullable(),
  categoria: resumoDaCategoriaSchema.nullable(),
  nome: z.string(),
  /** 0 = domingo … 6 = sábado, a convenção do `Date.getDay` que o motor usa. */
  diasDaSemana: z.array(z.number().int()),
  multiplicadorEmPontosBase: z.number().int(),
  ativo: z.boolean(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type RegraDeDiaDaSemana = z.infer<typeof regraDeDiaDaSemanaSchema>

export const paginaDeRegrasDeDiaDaSemanaSchema = paginaDe(regraDeDiaDaSemanaSchema)

export type PaginaDeRegrasDeDiaDaSemana = z.infer<typeof paginaDeRegrasDeDiaDaSemanaSchema>

// ---------------------------------------------------------------------------
// Camada 4 — antecedência
// ---------------------------------------------------------------------------

export const regraDeAntecedenciaSchema = z.object({
  id: z.uuid(),
  categoryId: z.uuid().nullable(),
  categoria: resumoDaCategoriaSchema.nullable(),
  nome: z.string(),
  minimoEmDias: z.number().int(),
  /** `null` = sem teto — é o early bird "de 30 dias em diante". */
  maximoEmDias: z.number().int().nullable(),
  multiplicadorEmPontosBase: z.number().int(),
  ativo: z.boolean(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type RegraDeAntecedencia = z.infer<typeof regraDeAntecedenciaSchema>

export const paginaDeRegrasDeAntecedenciaSchema = paginaDe(regraDeAntecedenciaSchema)

export type PaginaDeRegrasDeAntecedencia = z.infer<typeof paginaDeRegrasDeAntecedenciaSchema>

// ---------------------------------------------------------------------------
// Camada 5 — ocupação
// ---------------------------------------------------------------------------

export const regraDeOcupacaoSchema = z.object({
  id: z.uuid(),
  categoryId: z.uuid().nullable(),
  categoria: resumoDaCategoriaSchema.nullable(),
  nome: z.string(),
  /** Faixa de ocupação prevista da categoria, em pontos-base (5000 = 50%). */
  ocupacaoMinimaEmPontosBase: z.number().int(),
  ocupacaoMaximaEmPontosBase: z.number().int(),
  multiplicadorEmPontosBase: z.number().int(),
  ativo: z.boolean(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type RegraDeOcupacao = z.infer<typeof regraDeOcupacaoSchema>

export const paginaDeRegrasDeOcupacaoSchema = paginaDe(regraDeOcupacaoSchema)

export type PaginaDeRegrasDeOcupacao = z.infer<typeof paginaDeRegrasDeOcupacaoSchema>

// ---------------------------------------------------------------------------
// Política de locação
// ---------------------------------------------------------------------------

/**
 * A política de contagem de diária do tenant.
 *
 * `definida` existe pelo mesmo motivo de `GET /custos/politica`: a leitura NÃO
 * cria linha. Enquanto ninguém salvar a tela, a resposta traz os padrões do
 * schema com `definida: false` — e a tela precisa dizer que está mostrando
 * sugestão, não decisão. Um gestor que lê "tolerância: 0 min" achando que
 * alguém escolheu isso toma uma decisão de negócio sobre um valor de fábrica.
 */
export const politicaDeLocacaoSchema = z.object({
  definida: z.boolean(),
  toleranciaEmMinutos: z.number().int().nonnegative(),
  bufferEmHoras: z.number().int().nonnegative(),
  overbookingEmPontosBase: z.number().int().nonnegative(),
  /** Desligado por padrão: sem ele as regras de ocupação nem são consultadas. */
  precoPorOcupacaoAtivo: z.boolean(),
  validadeDaCotacaoEmMinutos: z.number().int().positive(),
  // Os três abaixo são obrigatórios de propósito: a API responde SEMPRE com o
  // default do schema shared, mesmo para linha salva antes deles existirem.
  prazoDePagamentoEmHoras: z.number().int().positive(),
  reembolsoDeDevolucaoAntecipadaPercentual: z.number().int().nonnegative(),
  /** `null` = no-show continua decisão manual do balcão. */
  noShowAposHoras: z.number().int().positive().nullable(),
  /** Multa pós-devolução (`multa-pos-devolucao.ts`). CONGELADA na reserva. */
  diasDeRetencaoDaCaucao: z.number().int().nonnegative(),
  cobrancaPosteriorDeMulta: z.boolean(),
  diasDeAvisoAntesDaCobranca: z.number().int().positive(),
  taxaDeMultaTipo: z.enum(TIPOS_DE_TAXA_DE_MULTA),
  taxaDeMultaPercentual: z.number().int().nonnegative(),
  /** Centavos, string. */
  taxaDeMultaFixa: z.string(),
  atualizadoEm: z.string().nullable(),
})

export type PoliticaDeLocacao = z.infer<typeof politicaDeLocacaoSchema>

// ---------------------------------------------------------------------------
// Simulação de diárias
// ---------------------------------------------------------------------------

/**
 * De onde saiu a tolerância que a simulação usou.
 *
 * Nasce aqui e não em `@locaveri/shared` porque é vocabulário da API: só o
 * servidor sabe resolver a cascata informada → categoria → política. A tela
 * mostra a origem porque "10 minutos" sem dizer de onde veio faz o gestor
 * ajustar a política e não entender por que aquela categoria não mudou.
 */
export const ORIGENS_DA_TOLERANCIA = ['INFORMADA', 'CATEGORIA', 'POLITICA'] as const

export type OrigemDaTolerancia = (typeof ORIGENS_DA_TOLERANCIA)[number]

export const ROTULO_DA_ORIGEM_DA_TOLERANCIA: Readonly<Record<OrigemDaTolerancia, string>> = {
  INFORMADA: 'Valor que você digitou',
  CATEGORIA: 'Tolerância da categoria',
  POLITICA: 'Política da locadora',
}

/**
 * O resultado da simulação.
 *
 * `diariasSemTolerancia` e `toleranciaMinimaParaEvitarADiaria` são o par que
 * transforma número em decisão: o primeiro é o que a locadora cobraria sem
 * cortesia nenhuma; o segundo responde "de quanto teria que ser a tolerância
 * para este atraso NÃO virar diária", que é a pergunta que o gestor está
 * fazendo ao abrir a tela. `null` quando nenhuma tolerância legal (abaixo de um
 * dia inteiro) evitaria a diária extra.
 */
export const simulacaoDeDiariasSchema = z.object({
  retirada: z.string(),
  devolucao: z.string(),
  timeZone: z.string(),
  toleranciaEmMinutos: z.number().int().nonnegative(),
  origemDaTolerancia: z.enum(ORIGENS_DA_TOLERANCIA),
  categoryId: z.uuid().nullable(),
  diarias: z.number().int().positive(),
  faixa: precificacao.chaveDaFaixaSchema,
  faixaRotulo: z.string(),
  diariasSemTolerancia: z.number().int().positive(),
  diariaExtraEvitada: z.boolean(),
  toleranciaMinimaParaEvitarADiaria: z.number().int().nonnegative().nullable(),
})

export type SimulacaoDeDiarias = z.infer<typeof simulacaoDeDiariasSchema>

// ---------------------------------------------------------------------------
// Corpos de escrita
// ---------------------------------------------------------------------------

/**
 * Corpo já validado por um schema de `@locaveri/shared`, pronto para o fio.
 *
 * É um mapa aberto pelo mesmo motivo de `CorpoDeVeiculo` na frota: os schemas
 * de entrada TRANSFORMAM — `moneySchemaPositive` devolve `bigint` (a diária da
 * tarifa, o valor fixo da sazonalidade) e `JSON.stringify` de `bigint` LANÇA.
 * O formulário roda o `safeParse` para VALIDAR e envia a entrada validada —
 * strings —, nunca `validado.data`. Tipar aqui como `z.infer` do schema
 * obrigaria uma conversão no diálogo só para agradar a inferência, e ainda
 * mandaria `bigint` para o `fetch`.
 */
type CorpoValidado = Readonly<Record<string, unknown>>

export type CorpoDeTabelaDePreco = CorpoValidado
export type CorpoDeTarifas = CorpoValidado
export type CorpoDeSazonalidade = CorpoValidado
export type CorpoDeRegraDePreco = CorpoValidado
export type CorpoDaPoliticaDeLocacao = CorpoValidado

// ---------------------------------------------------------------------------
// Filtros
// ---------------------------------------------------------------------------

/**
 * Filtro booleano de três estados.
 *
 * É string, e não `boolean | undefined`, porque `montarQuery` descarta `''` mas
 * ENVIA `false` como `"false"` — com booleano não haveria como dizer "tanto
 * faz". `''` = todos, `'true'` = só ativos, `'false'` = só inativos.
 */
export type FiltroDeAtivo = 'true' | 'false' | ''

export interface FiltrosDeTabelas {
  readonly pagina: number
  readonly tamanho: number
  readonly ativo: FiltroDeAtivo
  /** `'true'` isola a tabela da vitrine — a única padrão ativa do tenant. */
  readonly padrao: FiltroDeAtivo
  /** Só as tabelas vigentes nesta data de calendário (`AAAA-MM-DD`). */
  readonly vigenteEm: string
}

export interface FiltrosDeTarifas {
  readonly pagina: number
  readonly tamanho: number
  readonly priceTableId: string
  readonly categoryId: string
  readonly faixa: ChaveDaFaixa | ''
}

export interface FiltrosDeSazonalidades {
  readonly pagina: number
  readonly tamanho: number
  readonly priceTableId: string
  readonly categoryId: string
  readonly efeito: EfeitoDeRegra | ''
  readonly ativo: FiltroDeAtivo
  /** Só as regras que cobrem este dia — é como a tela explica um preço. */
  readonly vigenteEm: string
}

/**
 * Serve às três camadas simples (dia da semana, antecedência e ocupação): elas
 * filtram exatamente pelas mesmas coisas, e é o mesmo DTO do lado da API.
 */
export interface FiltrosDeRegras {
  readonly pagina: number
  readonly tamanho: number
  readonly categoryId: string
  readonly ativo: FiltroDeAtivo
}

/**
 * A pergunta do simulador. `retirada` e `devolucao` são ISO 8601 COM fuso — o
 * `instanteSchema` da API recusa string sem deslocamento, e o 422 apareceria
 * como "formato inválido" sem dizer o que falta.
 */
export interface ParametrosDaSimulacao {
  readonly retirada: string
  readonly devolucao: string
  /** Vazio usa a tolerância que valeria de verdade hoje; preenchido testa um valor. */
  readonly toleranciaEmMinutos: number | ''
  /** A categoria cujo override de tolerância vale. Vazio = a do tenant. */
  readonly categoryId: string
}

// ---------------------------------------------------------------------------
// Chaves de cache
//
// As chaves das DUAS metades da fase moram aqui, e não cada uma no seu arquivo.
// Salvar uma tarifa ou uma regra muda o PREÇO, e o preço aparece na cotação e
// no calendário: a invalidação atravessa a fronteira entre `api.ts` e
// `cotacao.ts`. Com as chaves espalhadas, os dois módulos importariam um do
// outro, e um ciclo de ESM entre `const` de topo é `undefined` na hora errada.
// A dependência é de mão única: `cotacao.ts` importa daqui.
// ---------------------------------------------------------------------------

export const CHAVE_DE_TABELAS = 'tabelas-de-preco'
export const CHAVE_DE_TARIFAS = 'tarifas'
export const CHAVE_DE_SAZONALIDADES = 'sazonalidades'
export const CHAVE_DE_DIAS_DA_SEMANA = 'regras-de-dia-da-semana'
export const CHAVE_DE_ANTECEDENCIAS = 'regras-de-antecedencia'
export const CHAVE_DE_OCUPACOES = 'regras-de-ocupacao'
export const CHAVE_DA_POLITICA_DE_LOCACAO = 'politica-de-locacao'
export const CHAVE_DA_SIMULACAO = 'simulacao-de-diarias'

export const CHAVE_DE_COTACOES = 'cotacoes'
export const CHAVE_DE_CAPACIDADE = 'capacidade'
export const CHAVE_DE_CALENDARIO = 'calendario-de-disponibilidade'
export const CHAVE_DE_BLOQUEIOS = 'bloqueios-de-veiculo'
export const CHAVE_DE_PRESTADORES = 'prestadores'
export const CHAVE_DE_ESCALA = 'escala-de-prestadores'
export const CHAVE_DE_WAITLIST = 'lista-de-espera'
export const CHAVE_DE_OVERBOOKING = 'overbooking'

// ---------------------------------------------------------------------------
// Invalidação
// ---------------------------------------------------------------------------

/**
 * Toda escrita de preço arrasta a cotação e o calendário.
 *
 * Uma tarifa nova, uma sazonalidade desativada, um multiplicador corrigido: o
 * número que a tela de cotação está exibindo virou passado no instante do
 * `PUT`. Invalidar só a lista da regra deixaria o gestor conferir o efeito da
 * mudança olhando para o preço de antes — e concluir que a regra não funciona.
 *
 * A cotação EMITIDA não muda: `QuoteSnapshot` é congelado e alterar a tabela
 * hoje não mexe no preço de ontem. O que fica velho é a cotação que a TELA
 * acabou de pedir, e é essa que sai do cache.
 *
 * O calendário entra na lista porque `diaDoCalendario` carrega `diaria` no
 * contrato: hoje ela vem `null` do endpoint de frota, e passa a vir preenchida
 * quando o módulo de cotações compuser a resposta. Deixar de invalidá-lo agora
 * criaria um bug que só apareceria naquele dia.
 */
function invalidarPreco(queryClient: QueryClient, ...chaves: readonly string[]): void {
  for (const chave of chaves) {
    void queryClient.invalidateQueries({ queryKey: [chave] })
  }

  void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_COTACOES] })
  void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CALENDARIO] })
}

// ---------------------------------------------------------------------------
// Tabelas — leitura
// ---------------------------------------------------------------------------

export function useTabelasDePreco(filtros: FiltrosDeTabelas): UseQueryResult<PaginaDeTabelas> {
  return useQuery({
    queryKey: [CHAVE_DE_TABELAS, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        ativo: filtros.ativo,
        padrao: filtros.padrao,
        vigenteEm: filtros.vigenteEm,
      })

      return apiGet(`${ROTAS_DE_PRECOS.tabelas}${query}`, paginaDeTabelasSchema, { signal })
    },
    // Mantém a página anterior enquanto a nova carrega: sem isso a tabela
    // pisca em branco a cada mudança de filtro.
    placeholderData: (anterior) => anterior,
  })
}

export function useTabelaDePreco(id: string | null): UseQueryResult<TabelaDePreco> {
  return useQuery({
    queryKey: [CHAVE_DE_TABELAS, id],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_PRECOS.tabelaPorId(id ?? ''), tabelaDePrecoSchema, { signal }),
    // Sem tabela selecionada não existe pergunta a fazer.
    enabled: id !== null,
  })
}

/**
 * As tabelas ATIVAS, para o seletor da grade de tarifas e das regras.
 *
 * Uma página só, no teto do servidor: uma locadora tem punhados de tabelas, não
 * milhares. Se um dia passar de `TAMANHO_MAXIMO_DE_PAGINA`, o seletor precisa
 * virar campo de busca — e o corte silencioso aqui é o sintoma.
 */
export function useTabelasAtivas(): UseQueryResult<PaginaDeTabelas> {
  return useQuery({
    queryKey: [CHAVE_DE_TABELAS, 'ativas'],
    queryFn: ({ signal }) => {
      const query = montarQuery({ pagina: 1, tamanho: TAMANHO_MAXIMO_DE_PAGINA, ativo: 'true' })

      return apiGet(`${ROTAS_DE_PRECOS.tabelas}${query}`, paginaDeTabelasSchema, { signal })
    },
  })
}

// ---------------------------------------------------------------------------
// Tabelas — escrita
// ---------------------------------------------------------------------------

export function useCriarTabelaDePreco(): UseMutationResult<
  TabelaDePreco,
  Error,
  CorpoDeTabelaDePreco
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeTabelaDePreco) =>
      apiPost(ROTAS_DE_PRECOS.tabelas, tabelaDePrecoSchema, dados),
    onSuccess: () => {
      invalidarPreco(queryClient, CHAVE_DE_TABELAS)
    },
  })
}

export interface AtualizacaoDeTabelaDePreco {
  readonly id: string
  readonly dados: CorpoDeTabelaDePreco
}

/**
 * Marcar uma tabela como padrão DESMARCA a outra do lado do servidor (índice
 * único parcial), e por isso a lista inteira sai do cache — não só a linha
 * editada. Sem isso a tela mostraria duas tabelas padrão até o próximo
 * `staleTime`, que é exatamente a inconsistência que o índice existe para
 * impedir.
 */
export function useAtualizarTabelaDePreco(): UseMutationResult<
  TabelaDePreco,
  Error,
  AtualizacaoDeTabelaDePreco
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDeTabelaDePreco) =>
      apiPatch(ROTAS_DE_PRECOS.tabelaPorId(id), tabelaDePrecoSchema, dados),
    onSuccess: () => {
      invalidarPreco(queryClient, CHAVE_DE_TABELAS, CHAVE_DE_TARIFAS)
    },
  })
}

/**
 * Desativa a tabela. NÃO apaga: cotação emitida aponta para ela, e o histórico
 * de preço é o que responde "por que cobramos isso em março".
 */
export function useDesativarTabelaDePreco(): UseMutationResult<TabelaDePreco, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => apiDelete(ROTAS_DE_PRECOS.tabelaPorId(id), tabelaDePrecoSchema),
    onSuccess: () => {
      invalidarPreco(queryClient, CHAVE_DE_TABELAS, CHAVE_DE_TARIFAS)
    },
  })
}

// ---------------------------------------------------------------------------
// Tarifas — leitura
// ---------------------------------------------------------------------------

export function useTarifas(filtros: FiltrosDeTarifas): UseQueryResult<PaginaDeTarifas> {
  return useQuery({
    queryKey: [CHAVE_DE_TARIFAS, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        priceTableId: filtros.priceTableId,
        categoryId: filtros.categoryId,
        faixa: filtros.faixa,
      })

      return apiGet(`${ROTAS_DE_PRECOS.tarifas}${query}`, paginaDeTarifasSchema, { signal })
    },
    placeholderData: (anterior) => anterior,
  })
}

/**
 * A grade da tabela: uma linha por categoria, as cinco faixas em ordem e as que
 * faltam. É esta a leitura da tela de preço — a listagem plana serve a busca,
 * não à edição.
 */
export function useTarifasDaTabela(priceTableId: string | null): UseQueryResult<TarifasDaTabela> {
  return useQuery({
    queryKey: [CHAVE_DE_TARIFAS, priceTableId, 'grade'],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_PRECOS.tarifasDaTabela(priceTableId ?? ''), tarifasDaTabelaSchema, {
        signal,
      }),
    enabled: priceTableId !== null,
  })
}

// ---------------------------------------------------------------------------
// Tarifas — escrita
// ---------------------------------------------------------------------------

export interface SalvamentoDeTarifas {
  readonly priceTableId: string
  /**
   * `{ categoryId, tarifas: [{ faixa, diaria }] }` já validado por
   * `salvarTarifasSchema` — e enviado como ENTRADA, porque o schema transforma
   * a diária em `bigint`.
   */
  readonly dados: CorpoDeTarifas
}

/**
 * Grava TODAS as faixas de uma categoria de uma vez (`PUT`, substituição).
 *
 * Não é `PATCH` linha a linha de propósito: "a diária não sobe ao alongar a
 * locação" é propriedade do CONJUNTO. Editar faixa por faixa faria a tabela
 * passar por estados inválidos que ninguém observa — e um deles cotaria.
 *
 * A resposta é a linha de UMA categoria (`TarifasDaCategoria`), não a grade
 * inteira: o `PUT` mexeu numa categoria só, e devolver a tabela completa faria
 * o servidor recarregar o catálogo a cada faixa salva. A grade da tela se
 * atualiza pela invalidação abaixo.
 */
export function useSalvarTarifas(): UseMutationResult<
  TarifasDaCategoria,
  Error,
  SalvamentoDeTarifas
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ priceTableId, dados }: SalvamentoDeTarifas) =>
      apiRequest(ROTAS_DE_PRECOS.tarifasDaTabela(priceTableId), {
        schema: tarifasDaCategoriaSchema,
        method: 'PUT',
        body: dados,
      }),
    onSuccess: () => {
      // A tabela carrega a contagem de tarifas e de categorias precificadas.
      invalidarPreco(queryClient, CHAVE_DE_TARIFAS, CHAVE_DE_TABELAS)
    },
  })
}

export interface RemocaoDeTarifas {
  readonly priceTableId: string
  readonly categoryId: string
}

/**
 * Tira a categoria da tabela — as faixas todas juntas, nunca uma só. A resposta
 * é a linha da categoria já vazia, com todas as faixas em `faixasFaltantes`.
 */
export function useRemoverTarifasDaCategoria(): UseMutationResult<
  TarifasDaCategoria,
  Error,
  RemocaoDeTarifas
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ priceTableId, categoryId }: RemocaoDeTarifas) =>
      apiDelete(
        ROTAS_DE_PRECOS.tarifasDaCategoria(priceTableId, categoryId),
        tarifasDaCategoriaSchema,
      ),
    onSuccess: () => {
      invalidarPreco(queryClient, CHAVE_DE_TARIFAS, CHAVE_DE_TABELAS)
    },
  })
}

// ---------------------------------------------------------------------------
// Sazonalidades
// ---------------------------------------------------------------------------

export function useSazonalidades(
  filtros: FiltrosDeSazonalidades,
): UseQueryResult<PaginaDeSazonalidades> {
  return useQuery({
    queryKey: [CHAVE_DE_SAZONALIDADES, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        priceTableId: filtros.priceTableId,
        categoryId: filtros.categoryId,
        efeito: filtros.efeito,
        ativo: filtros.ativo,
        vigenteEm: filtros.vigenteEm,
      })

      return apiGet(`${ROTAS_DE_PRECOS.sazonalidades}${query}`, paginaDeSazonalidadesSchema, {
        signal,
      })
    },
    placeholderData: (anterior) => anterior,
  })
}

export function useCriarSazonalidade(): UseMutationResult<
  Sazonalidade,
  Error,
  CorpoDeSazonalidade
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeSazonalidade) =>
      apiPost(ROTAS_DE_PRECOS.sazonalidades, sazonalidadeSchema, dados),
    onSuccess: () => {
      // A tabela mostra quantas sazonalidades estão penduradas nela.
      invalidarPreco(queryClient, CHAVE_DE_SAZONALIDADES, CHAVE_DE_TABELAS)
    },
  })
}

export interface AtualizacaoDeSazonalidade {
  readonly id: string
  readonly dados: CorpoDeSazonalidade
}

export function useAtualizarSazonalidade(): UseMutationResult<
  Sazonalidade,
  Error,
  AtualizacaoDeSazonalidade
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDeSazonalidade) =>
      apiPatch(ROTAS_DE_PRECOS.sazonalidadePorId(id), sazonalidadeSchema, dados),
    onSuccess: () => {
      invalidarPreco(queryClient, CHAVE_DE_SAZONALIDADES, CHAVE_DE_TABELAS)
    },
  })
}

export function useDesativarSazonalidade(): UseMutationResult<Sazonalidade, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiDelete(ROTAS_DE_PRECOS.sazonalidadePorId(id), sazonalidadeSchema),
    onSuccess: () => {
      invalidarPreco(queryClient, CHAVE_DE_SAZONALIDADES, CHAVE_DE_TABELAS)
    },
  })
}

// ---------------------------------------------------------------------------
// Regras de dia da semana
// ---------------------------------------------------------------------------

export function useRegrasDeDiaDaSemana(
  filtros: FiltrosDeRegras,
): UseQueryResult<PaginaDeRegrasDeDiaDaSemana> {
  return useQuery({
    queryKey: [CHAVE_DE_DIAS_DA_SEMANA, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        categoryId: filtros.categoryId,
        ativo: filtros.ativo,
      })

      return apiGet(`${ROTAS_DE_PRECOS.diasDaSemana}${query}`, paginaDeRegrasDeDiaDaSemanaSchema, {
        signal,
      })
    },
    placeholderData: (anterior) => anterior,
  })
}

export function useCriarRegraDeDiaDaSemana(): UseMutationResult<
  RegraDeDiaDaSemana,
  Error,
  CorpoDeRegraDePreco
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeRegraDePreco) =>
      apiPost(ROTAS_DE_PRECOS.diasDaSemana, regraDeDiaDaSemanaSchema, dados),
    onSuccess: () => {
      invalidarPreco(queryClient, CHAVE_DE_DIAS_DA_SEMANA)
    },
  })
}

export interface AtualizacaoDeRegraDeDiaDaSemana {
  readonly id: string
  readonly dados: CorpoDeRegraDePreco
}

export function useAtualizarRegraDeDiaDaSemana(): UseMutationResult<
  RegraDeDiaDaSemana,
  Error,
  AtualizacaoDeRegraDeDiaDaSemana
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDeRegraDeDiaDaSemana) =>
      apiPatch(ROTAS_DE_PRECOS.diaDaSemanaPorId(id), regraDeDiaDaSemanaSchema, dados),
    onSuccess: () => {
      invalidarPreco(queryClient, CHAVE_DE_DIAS_DA_SEMANA)
    },
  })
}

export function useDesativarRegraDeDiaDaSemana(): UseMutationResult<
  RegraDeDiaDaSemana,
  Error,
  string
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiDelete(ROTAS_DE_PRECOS.diaDaSemanaPorId(id), regraDeDiaDaSemanaSchema),
    onSuccess: () => {
      invalidarPreco(queryClient, CHAVE_DE_DIAS_DA_SEMANA)
    },
  })
}

// ---------------------------------------------------------------------------
// Regras de antecedência
// ---------------------------------------------------------------------------

export function useRegrasDeAntecedencia(
  filtros: FiltrosDeRegras,
): UseQueryResult<PaginaDeRegrasDeAntecedencia> {
  return useQuery({
    queryKey: [CHAVE_DE_ANTECEDENCIAS, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        categoryId: filtros.categoryId,
        ativo: filtros.ativo,
      })

      return apiGet(
        `${ROTAS_DE_PRECOS.antecedencias}${query}`,
        paginaDeRegrasDeAntecedenciaSchema,
        { signal },
      )
    },
    placeholderData: (anterior) => anterior,
  })
}

export function useCriarRegraDeAntecedencia(): UseMutationResult<
  RegraDeAntecedencia,
  Error,
  CorpoDeRegraDePreco
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeRegraDePreco) =>
      apiPost(ROTAS_DE_PRECOS.antecedencias, regraDeAntecedenciaSchema, dados),
    onSuccess: () => {
      invalidarPreco(queryClient, CHAVE_DE_ANTECEDENCIAS)
    },
  })
}

export interface AtualizacaoDeRegraDeAntecedencia {
  readonly id: string
  readonly dados: CorpoDeRegraDePreco
}

export function useAtualizarRegraDeAntecedencia(): UseMutationResult<
  RegraDeAntecedencia,
  Error,
  AtualizacaoDeRegraDeAntecedencia
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDeRegraDeAntecedencia) =>
      apiPatch(ROTAS_DE_PRECOS.antecedenciaPorId(id), regraDeAntecedenciaSchema, dados),
    onSuccess: () => {
      invalidarPreco(queryClient, CHAVE_DE_ANTECEDENCIAS)
    },
  })
}

export function useDesativarRegraDeAntecedencia(): UseMutationResult<
  RegraDeAntecedencia,
  Error,
  string
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiDelete(ROTAS_DE_PRECOS.antecedenciaPorId(id), regraDeAntecedenciaSchema),
    onSuccess: () => {
      invalidarPreco(queryClient, CHAVE_DE_ANTECEDENCIAS)
    },
  })
}

// ---------------------------------------------------------------------------
// Regras de ocupação
// ---------------------------------------------------------------------------

export function useRegrasDeOcupacao(
  filtros: FiltrosDeRegras,
): UseQueryResult<PaginaDeRegrasDeOcupacao> {
  return useQuery({
    queryKey: [CHAVE_DE_OCUPACOES, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        categoryId: filtros.categoryId,
        ativo: filtros.ativo,
      })

      return apiGet(`${ROTAS_DE_PRECOS.ocupacoes}${query}`, paginaDeRegrasDeOcupacaoSchema, {
        signal,
      })
    },
    placeholderData: (anterior) => anterior,
  })
}

export function useCriarRegraDeOcupacao(): UseMutationResult<
  RegraDeOcupacao,
  Error,
  CorpoDeRegraDePreco
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeRegraDePreco) =>
      apiPost(ROTAS_DE_PRECOS.ocupacoes, regraDeOcupacaoSchema, dados),
    onSuccess: () => {
      invalidarPreco(queryClient, CHAVE_DE_OCUPACOES)
    },
  })
}

export interface AtualizacaoDeRegraDeOcupacao {
  readonly id: string
  readonly dados: CorpoDeRegraDePreco
}

export function useAtualizarRegraDeOcupacao(): UseMutationResult<
  RegraDeOcupacao,
  Error,
  AtualizacaoDeRegraDeOcupacao
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDeRegraDeOcupacao) =>
      apiPatch(ROTAS_DE_PRECOS.ocupacaoPorId(id), regraDeOcupacaoSchema, dados),
    onSuccess: () => {
      invalidarPreco(queryClient, CHAVE_DE_OCUPACOES)
    },
  })
}

export function useDesativarRegraDeOcupacao(): UseMutationResult<RegraDeOcupacao, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => apiDelete(ROTAS_DE_PRECOS.ocupacaoPorId(id), regraDeOcupacaoSchema),
    onSuccess: () => {
      invalidarPreco(queryClient, CHAVE_DE_OCUPACOES)
    },
  })
}

// ---------------------------------------------------------------------------
// Política de locação
// ---------------------------------------------------------------------------

export function usePoliticaDeLocacao(): UseQueryResult<PoliticaDeLocacao> {
  return useQuery({
    queryKey: [CHAVE_DA_POLITICA_DE_LOCACAO],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_PRECOS.politicaDeLocacao, politicaDeLocacaoSchema, { signal }),
  })
}

/**
 * Salva a política (upsert — é uma linha por tenant, daí o `PUT`).
 *
 * Invalida muito mais do que a própria leitura, e cada item tem motivo: a
 * TOLERÂNCIA muda quantas diárias um período dá (simulação e cotação), o BUFFER
 * muda a janela que a disponibilidade dilata nas duas pontas (capacidade), o
 * OVERBOOKING muda quantas unidades a mais podem ser vendidas e o interruptor de
 * preço por ocupação decide se a quinta camada é sequer consultada.
 *
 * Cotação já emitida NÃO muda: cada `QuoteSnapshot` congelou a tolerância que
 * valia no momento, e é com ela que a Fase 4 fatura.
 */
export function useSalvarPoliticaDeLocacao(): UseMutationResult<
  PoliticaDeLocacao,
  Error,
  CorpoDaPoliticaDeLocacao
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDaPoliticaDeLocacao) =>
      apiRequest(ROTAS_DE_PRECOS.politicaDeLocacao, {
        schema: politicaDeLocacaoSchema,
        method: 'PUT',
        body: dados,
      }),
    onSuccess: () => {
      invalidarPreco(
        queryClient,
        CHAVE_DA_POLITICA_DE_LOCACAO,
        CHAVE_DA_SIMULACAO,
        CHAVE_DE_CAPACIDADE,
      )
    },
  })
}

// ---------------------------------------------------------------------------
// Simulação de diárias
// ---------------------------------------------------------------------------

/**
 * "Devolvendo às 10h05 de quinta, quantas diárias o cliente paga?"
 *
 * Fica desligada até o período estar completo: com uma ponta só, a API
 * responderia 422 e a tela mostraria erro de validação para quem ainda está
 * preenchendo o formulário.
 */
export function useSimulacaoDeDiarias(
  parametros: ParametrosDaSimulacao,
): UseQueryResult<SimulacaoDeDiarias> {
  return useQuery({
    queryKey: [CHAVE_DA_SIMULACAO, parametros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        retirada: parametros.retirada,
        devolucao: parametros.devolucao,
        toleranciaEmMinutos: parametros.toleranciaEmMinutos,
        categoryId: parametros.categoryId,
      })

      return apiGet(`${ROTAS_DE_PRECOS.simularDiarias}${query}`, simulacaoDeDiariasSchema, {
        signal,
      })
    },
    enabled: parametros.retirada.length > 0 && parametros.devolucao.length > 0,
    placeholderData: (anterior) => anterior,
  })
}

// ---------------------------------------------------------------------------
// Apresentação
//
// Pontos-base NÃO são dinheiro: são inteiros de razão (10000 = 1,0×), e por isso
// a conta abaixo pode acontecer em `number` sem violar o invariante nº 1. O que
// nunca acontece aqui é multiplicar uma diária por um multiplicador — esse
// número vem pronto do motor.
// ---------------------------------------------------------------------------

/** `12500` → `1,25×`. `10000` → `1×` (neutro, não mexe no preço). */
export function formatarMultiplicador(pontosBase: number): string {
  const fator = pontosBase / PONTOS_BASE_NEUTROS

  return `${fator.toLocaleString('pt-BR', { maximumFractionDigits: 4 })}×`
}

/**
 * O mesmo multiplicador dito como variação: `12500` → `+25%`, `9000` → `−20%`.
 *
 * Existe ao lado de `formatarMultiplicador` porque as duas leituras respondem
 * perguntas diferentes — "que fator é este" e "encarece ou barateia quanto" —, e
 * a segunda é a que o gestor confere ao criar a regra. O sinal usa o menos
 * tipográfico (U+2212), que alinha com os dígitos em `tabular-nums`.
 */
export function formatarVariacaoDoMultiplicador(pontosBase: number): string {
  const variacao = (pontosBase - PONTOS_BASE_NEUTROS) / 100

  if (variacao === 0) {
    return 'sem efeito'
  }

  const absoluto = Math.abs(variacao).toLocaleString('pt-BR', { maximumFractionDigits: 2 })

  return `${variacao > 0 ? '+' : '−'}${absoluto}%`
}

/** `5000` → `50%`. É a escala das faixas de ocupação. */
export function formatarPontosBaseComoPercentual(pontosBase: number): string {
  return `${(pontosBase / 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`
}

/**
 * Regra sem categoria vale para TODAS — e dizer isso por extenso importa: um
 * traço nessa coluna seria lido como "faltou preencher", que é o oposto do
 * significado. O mesmo vale para a tabela de preço na sazonalidade.
 */
export function escopoDaRegra(categoria: ResumoDaCategoria | null): string {
  return categoria === null ? 'Todas as categorias' : categoria.nome
}

const DIAS_ABREVIADOS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const

/**
 * `[5, 6]` → `sex, sáb`.
 *
 * Ordena antes de escrever: o formulário permite marcar sábado antes de sexta, e
 * `sáb, sex` em toda linha da tabela faria a coluna parecer aleatória. Índice
 * fora de 0–6 vira traço em vez de `undefined` — o dado veio de fora.
 */
export function rotuloDosDiasDaSemana(dias: readonly number[]): string {
  if (dias.length === 0) {
    return SEM_VALOR
  }

  return [...dias]
    .sort((umDia, outroDia) => umDia - outroDia)
    .map((dia) => DIAS_ABREVIADOS[dia] ?? SEM_VALOR)
    .join(', ')
}

/**
 * `{ minimoEmDias: 30, maximoEmDias: null }` → `30 dias ou mais`.
 *
 * O teto ausente é o early bird "de 30 dias em diante", e escrevê-lo como
 * `30 – —` deixaria a linha ilegível justamente na regra mais usada.
 */
export function faixaDeAntecedencia(regra: {
  readonly minimoEmDias: number
  readonly maximoEmDias: number | null
}): string {
  const minimo = String(regra.minimoEmDias)

  if (regra.maximoEmDias === null) {
    return `${minimo} dias ou mais`
  }

  return `${minimo} a ${String(regra.maximoEmDias)} dias`
}

/**
 * A vigência da tabela em uma linha: `01/03/2026 → sem prazo`.
 *
 * As datas são de CALENDÁRIO (`AAAA-MM-DD`), e `formatarData` sabe disso: para
 * um dia ele fatia a string, e só converte fuso quando o fio manda um INSTANTE.
 * Esta tela já teve a sua própria cópia da regra — `formatarDataSimples` — de
 * volta ao tempo em que `formatarData` convertia tudo e a tabela de março
 * aparecia começando em fevereiro.
 */
export function vigenciaEmTexto(vigenteDe: string, vigenteAte: string | null): string {
  const inicio = formatarData(vigenteDe)
  const fim = vigenteAte === null ? 'sem prazo' : formatarData(vigenteAte)

  return `${inicio} → ${fim}`
}
